import cn from 'classnames';
import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { InputLabel, Modal, Textarea } from '@/shared/ui';

import {
  type AiInstructionsTarget,
  templateEditorMessages,
} from '../../config';

import styles from './AiInstructionsModal.module.scss';

type AiInstructionsModalProps = {
  isOpen: boolean;
  target: AiInstructionsTarget | null;
  instructions: string;
  onSave: (instructions: string) => void;
  onClose: () => void;
};

export const AiInstructionsModal: FC<AiInstructionsModalProps> = (props) => {
  const { isOpen, target, instructions, onSave, onClose } = props;

  const { formatMessage } = useIntl();

  const [draft, setDraft] = useState(instructions);

  useEffect(() => {
    if (isOpen) {
      setDraft(instructions);
    }
  }, [isOpen, instructions]);

  const targetLabel = target
    ? target.kind === 'section'
      ? formatMessage(templateEditorMessages.aiInstructionsForSection, {
          number: target.sectionIndex + 1,
        })
      : formatMessage(templateEditorMessages.aiInstructionsForOption, {
          number: `${target.sectionIndex + 1}.${target.optionIndex + 1}`,
        })
    : '';

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setDraft(event.target.value);
  };

  const aiInstructionsLabel = formatMessage(
    templateEditorMessages.aiInstructionsTitle,
  );

  return (
    <Modal
      isOpen={isOpen}
      borderless
      dataComponent="ai-instructions-modal"
      title={aiInstructionsLabel}
      okButtonText={formatMessage(templateEditorMessages.save)}
      cancelButtonText={formatMessage(templateEditorMessages.cancel)}
      onOk={() => onSave(draft.trim())}
      onCancel={onClose}
    >
      <div className={styles.body}>
        <span className={cn('p3', styles.target)}>{targetLabel}</span>
        <div className={styles.field}>
          <InputLabel label={aiInstructionsLabel} />
          <Textarea
            value={draft}
            rows={4}
            placeholder={aiInstructionsLabel}
            onChange={handleChange}
          />
        </div>
      </div>
    </Modal>
  );
};
